import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import useCompanies from "../../api/company";
import PopupsManager from "./PopupsManager";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Building2 } from "lucide-react";

const AdminPopupsManager = () => {
  const { isAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { getAllCompanies } = useCompanies();

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await getAllCompanies();
        setCompanies(data || []);
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) {
      fetchCompanies();
    }
  }, [isAdmin, getAllCompanies]);

  if (!isAdmin) {
    return (
      <div
        className={`p-4 text-[13px] ${isDark ? "text-gray-400" : "text-gray-500"}`}
      >
        Toegang geweigerd. Alleen voor administrators.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1
          className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
        >
          Admin Pop-ups Beheer
        </h1>
        <p
          className={`text-[13px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
        >
          Beheer pop-ups voor andere bedrijven.
        </p>
      </div>

      <div
        className={`rounded-xl p-5 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <Building2
            className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
          />
          <span
            className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            Selecteer Bedrijf
          </span>
        </div>
        <Select
          value={selectedCompanyId ? String(selectedCompanyId) : ""}
          onValueChange={(value) => setSelectedCompanyId(value)}
        >
          <SelectTrigger
            className={`w-full md:w-[300px] h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
          >
            <SelectValue placeholder="Kies een bedrijf..." />
          </SelectTrigger>
          <SelectContent
            className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
          >
            {companies.map((company) => (
              <SelectItem key={company.id} value={String(company.id)}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCompanyId ? (
        <div>
          <div className="mb-4 flex items-center gap-2">
            <span
              className={`text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Geselecteerd:
            </span>
            <span
              className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {
                companies.find(
                  (c) => String(c.id) === String(selectedCompanyId),
                )?.name
              }
            </span>
          </div>
          <div
            className={`border-t pt-6 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <PopupsManager
              key={selectedCompanyId}
              targetCompanyId={selectedCompanyId}
            />
          </div>
        </div>
      ) : (
        <div
          className={`flex flex-col items-center justify-center py-14 text-center rounded-xl border-2 border-dashed ${isDark ? "border-white/[0.06] bg-white/[0.01]" : "border-gray-200 bg-gray-50/50"}`}
        >
          <Building2
            className={`h-10 w-10 mb-3 ${isDark ? "text-gray-700" : "text-gray-300"}`}
          />
          <h3
            className={`text-[14px] font-semibold ${isDark ? "text-gray-300" : "text-gray-900"}`}
          >
            Geen bedrijf geselecteerd
          </h3>
          <p
            className={`text-[13px] max-w-sm mt-1 ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            Selecteer een bedrijf uit het menu hierboven om de pop-ups te
            beheren.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminPopupsManager;
